/* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params, prefer-spread */
// Centralized Ad Pixel Tracking for Meta, TikTok, Google Ads, and Bing/Microsoft.
//
// Pixel IDs come from public Vite env vars (safe to expose in the browser bundle):
//   VITE_META_PIXEL_ID        — Meta Pixel / Dataset ID (Events Manager)
//   VITE_TIKTOK_PIXEL_ID      — TikTok Pixel ID
//   VITE_GOOGLE_ADS_ID        — Google Ads conversion ID (AW-XXXXXXXXX)
//   VITE_GOOGLE_PURCHASE_LABEL / VITE_GOOGLE_ADD_TO_CART_LABEL
//   VITE_BING_UET_ID          — Microsoft UET tag ID
//
// Marketing pixels only initialize AFTER the visitor accepts marketing cookies
// (see src/lib/consent.ts). With no consent or no IDs configured, every tracker
// below is a silent no-op.

import { hasMarketingConsent } from "@/lib/consent";

// ============================================
// PIXEL CONFIGURATION (from env)
// ============================================
export const PIXEL_CONFIG = {
  FACEBOOK_PIXEL_ID: import.meta.env.VITE_META_PIXEL_ID ?? '',
  TIKTOK_PIXEL_ID: import.meta.env.VITE_TIKTOK_PIXEL_ID ?? '',
  GOOGLE_ADS_ID: import.meta.env.VITE_GOOGLE_ADS_ID ?? '',
  GOOGLE_CONVERSION_LABELS: {
    purchase: import.meta.env.VITE_GOOGLE_PURCHASE_LABEL ?? '',
    add_to_cart: import.meta.env.VITE_GOOGLE_ADD_TO_CART_LABEL ?? '',
  },
  BING_UET_ID: import.meta.env.VITE_BING_UET_ID ?? '',
} as const;

const isConfigured = (value: string) => Boolean(value) && !value.startsWith('YOUR_');

// Tracking requires both an opt-in consent choice AND a configured ID.
const canTrack = (value: string) => hasMarketingConsent() && isConfigured(value);

// ============================================
// TYPES
// ============================================
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    ttq: any;
    gtag: any;
    dataLayer: any[];
    uetq: any[];
  }
}

// ============================================
// FACEBOOK PIXEL
// ============================================
export const facebookPixel = {
  isLoaded: () => typeof window !== 'undefined' && typeof window.fbq === 'function',

  pageView: () => {
    if (!canTrack(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) return;
    if (facebookPixel.isLoaded()) window.fbq('track', 'PageView');
  },

  addToCart: (value: number, currency = 'USD', contentName?: string) => {
    if (!canTrack(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) return;
    if (facebookPixel.isLoaded()) {
      window.fbq('track', 'AddToCart', { value, currency, content_name: contentName });
    }
  },

  initiateCheckout: (value: number, numItems: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) return;
    if (facebookPixel.isLoaded()) {
      window.fbq('track', 'InitiateCheckout', { value, currency, num_items: numItems });
    }
  },

  // eventID enables browser/server deduplication with Meta CAPI — both sides
  // must send the SAME eventID for the same order (e.g. purchase-{sessionId}).
  purchase: (value: number, currency = 'USD', orderId?: string, eventID?: string) => {
    if (!canTrack(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) return;
    if (facebookPixel.isLoaded()) {
      window.fbq(
        'track',
        'Purchase',
        { value, currency, order_id: orderId },
        eventID ? { eventID } : undefined,
      );
    }
  },

  viewContent: (contentName: string, contentId: string, value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) return;
    if (facebookPixel.isLoaded()) {
      window.fbq('track', 'ViewContent', {
        content_name: contentName,
        content_ids: [contentId],
        content_type: 'product',
        value,
        currency,
      });
    }
  },
};

// ============================================
// TIKTOK PIXEL
// ============================================
export const tiktokPixel = {
  isLoaded: () => typeof window !== 'undefined' && typeof window.ttq?.track === 'function',

  pageView: () => {
    if (!canTrack(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) return;
    if (tiktokPixel.isLoaded()) window.ttq.page();
  },

  addToCart: (value: number, currency = 'USD', contentName?: string) => {
    if (!canTrack(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) return;
    if (tiktokPixel.isLoaded()) {
      window.ttq.track('AddToCart', { value, currency, content_name: contentName });
    }
  },

  initiateCheckout: (value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) return;
    if (tiktokPixel.isLoaded()) window.ttq.track('InitiateCheckout', { value, currency });
  },

  purchase: (value: number, currency = 'USD', orderId?: string) => {
    if (!canTrack(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) return;
    if (tiktokPixel.isLoaded()) {
      window.ttq.track('CompletePayment', { value, currency, order_id: orderId });
    }
  },

  viewContent: (contentName: string, contentId: string, value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) return;
    if (tiktokPixel.isLoaded()) {
      window.ttq.track('ViewContent', { content_name: contentName, content_id: contentId, value, currency });
    }
  },
};

// ============================================
// GOOGLE ADS (gtag.js)
// ============================================
export const googleAds = {
  isLoaded: () => typeof window !== 'undefined' && typeof window.gtag === 'function',

  pageView: () => {
    if (!canTrack(PIXEL_CONFIG.GOOGLE_ADS_ID)) return;
    if (googleAds.isLoaded()) window.gtag('event', 'page_view');
  },

  addToCart: (value: number, currency = 'USD', items?: any[]) => {
    if (!canTrack(PIXEL_CONFIG.GOOGLE_ADS_ID)) return;
    if (googleAds.isLoaded()) {
      window.gtag('event', 'add_to_cart', { currency, value, items });
      if (isConfigured(PIXEL_CONFIG.GOOGLE_CONVERSION_LABELS.add_to_cart)) {
        window.gtag('event', 'conversion', {
          send_to: `${PIXEL_CONFIG.GOOGLE_ADS_ID}/${PIXEL_CONFIG.GOOGLE_CONVERSION_LABELS.add_to_cart}`,
          value,
          currency,
        });
      }
    }
  },

  initiateCheckout: (value: number, currency = 'USD', items?: any[]) => {
    if (!canTrack(PIXEL_CONFIG.GOOGLE_ADS_ID)) return;
    if (googleAds.isLoaded()) window.gtag('event', 'begin_checkout', { currency, value, items });
  },

  purchase: (value: number, currency = 'USD', transactionId?: string, items?: any[]) => {
    if (!canTrack(PIXEL_CONFIG.GOOGLE_ADS_ID)) return;
    if (googleAds.isLoaded()) {
      window.gtag('event', 'purchase', { transaction_id: transactionId, value, currency, items });
      if (isConfigured(PIXEL_CONFIG.GOOGLE_CONVERSION_LABELS.purchase)) {
        window.gtag('event', 'conversion', {
          send_to: `${PIXEL_CONFIG.GOOGLE_ADS_ID}/${PIXEL_CONFIG.GOOGLE_CONVERSION_LABELS.purchase}`,
          value,
          currency,
          transaction_id: transactionId,
        });
      }
    }
  },

  viewContent: (contentName: string, contentId: string, value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.GOOGLE_ADS_ID)) return;
    if (googleAds.isLoaded()) {
      window.gtag('event', 'view_item', {
        currency,
        value,
        items: [{ item_id: contentId, item_name: contentName, price: value }],
      });
    }
  },
};

// ============================================
// MICROSOFT/BING UET
// ============================================
export const bingAds = {
  isLoaded: () => typeof window !== 'undefined' && Array.isArray(window.uetq),

  pageView: () => {
    if (!canTrack(PIXEL_CONFIG.BING_UET_ID)) return;
    if (bingAds.isLoaded()) window.uetq.push('pageLoad');
  },

  addToCart: (value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.BING_UET_ID)) return;
    if (bingAds.isLoaded()) window.uetq.push('event', 'add_to_cart', { revenue_value: value, currency });
  },

  initiateCheckout: (value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.BING_UET_ID)) return;
    if (bingAds.isLoaded()) window.uetq.push('event', 'begin_checkout', { revenue_value: value, currency });
  },

  purchase: (value: number, currency = 'USD', transactionId?: string) => {
    if (!canTrack(PIXEL_CONFIG.BING_UET_ID)) return;
    if (bingAds.isLoaded()) {
      window.uetq.push('event', 'purchase', { revenue_value: value, currency, transaction_id: transactionId });
    }
  },

  viewContent: (contentName: string, value: number, currency = 'USD') => {
    if (!canTrack(PIXEL_CONFIG.BING_UET_ID)) return;
    if (bingAds.isLoaded()) {
      window.uetq.push('event', 'view_item', { revenue_value: value, currency, page_title: contentName });
    }
  },
};

// ============================================
// UNIFIED TRACKING API
// ============================================
export const trackingPixels = {
  pageView: () => {
    facebookPixel.pageView();
    tiktokPixel.pageView();
    googleAds.pageView();
    bingAds.pageView();
  },

  viewContent: (productName: string, productId: string, price: number) => {
    facebookPixel.viewContent(productName, productId, price);
    tiktokPixel.viewContent(productName, productId, price);
    googleAds.viewContent(productName, productId, price);
    bingAds.viewContent(productName, price);
  },

  addToCart: (productName: string, price: number, quantity: number = 1) => {
    const value = price * quantity;
    facebookPixel.addToCart(value, 'USD', productName);
    tiktokPixel.addToCart(value, 'USD', productName);
    googleAds.addToCart(value, 'USD', [{ item_name: productName, price, quantity }]);
    bingAds.addToCart(value);
  },

  initiateCheckout: (totalValue: number, itemCount: number) => {
    facebookPixel.initiateCheckout(totalValue, itemCount);
    tiktokPixel.initiateCheckout(totalValue);
    googleAds.initiateCheckout(totalValue);
    bingAds.initiateCheckout(totalValue);
  },

  // eventID ties the browser Purchase to the server-side CAPI Purchase for
  // deduplication (Meta drops the duplicate). Other pixels don't need it.
  purchase: (totalValue: number, orderId?: string, eventID?: string) => {
    facebookPixel.purchase(totalValue, 'USD', orderId, eventID);
    tiktokPixel.purchase(totalValue, 'USD', orderId);
    googleAds.purchase(totalValue, 'USD', orderId);
    bingAds.purchase(totalValue, orderId);
  },
};

// Export configuration status for debugging
export const getPixelStatus = () => ({
  consent: hasMarketingConsent(),
  facebook: isConfigured(PIXEL_CONFIG.FACEBOOK_PIXEL_ID),
  tiktok: isConfigured(PIXEL_CONFIG.TIKTOK_PIXEL_ID),
  google: isConfigured(PIXEL_CONFIG.GOOGLE_ADS_ID),
  bing: isConfigured(PIXEL_CONFIG.BING_UET_ID),
});

// ============================================
// INIT (call only after marketing consent)
// Defines the queue stubs AND injects the SDK scripts — nothing is loaded
// before this runs.
// ============================================
let pixelsInitialized = false;
export const initPixels = () => {
  if (pixelsInitialized || typeof window === 'undefined') return;
  if (!hasMarketingConsent()) return;
  pixelsInitialized = true;

  // Meta / Facebook Pixel — define stub, load fbevents.js, init.
  if (isConfigured(PIXEL_CONFIG.FACEBOOK_PIXEL_ID)) {
    try {
      const w = window as any;
      if (typeof w.fbq !== 'function') {
        const n: any = (w.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
        });
        if (!w._fbq) w._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const s = document.createElement('script');
        s.async = true;
        s.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(s);
      }
      window.fbq('init', PIXEL_CONFIG.FACEBOOK_PIXEL_ID);
      window.fbq('track', 'PageView');
    } catch (e) {
      console.warn('[Pixels] Facebook init failed', e);
    }
  }

  // TikTok Pixel
  if (isConfigured(PIXEL_CONFIG.TIKTOK_PIXEL_ID)) {
    try {
      const w = window as any;
      if (!w.ttq) {
        w.TiktokAnalyticsObject = 'ttq';
        const ttq = (w.ttq = w.ttq || []);
        ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
        ttq.setAndDefer = (t: any, e: string) => {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        };
        for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq._i = ttq._i || {};
        ttq._t = ttq._t || {};
        ttq._o = ttq._o || {};
        ttq.load = function (e: string, n?: any) {
          const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t[e] = +new Date();
          ttq._o[e] = n || {};
          const o = document.createElement('script');
          o.type = 'text/javascript';
          o.async = true;
          o.src = i + '?sdkid=' + e + '&lib=ttq';
          document.head.appendChild(o);
        };
      }
      window.ttq.load(PIXEL_CONFIG.TIKTOK_PIXEL_ID);
      window.ttq.page();
    } catch (e) {
      console.warn('[Pixels] TikTok init failed', e);
    }
  }

  // Google Ads (gtag)
  if (isConfigured(PIXEL_CONFIG.GOOGLE_ADS_ID)) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      const existing = document.querySelector(
        `script[src*="googletagmanager.com/gtag/js?id=${PIXEL_CONFIG.GOOGLE_ADS_ID}"]`,
      );
      if (!existing) {
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${PIXEL_CONFIG.GOOGLE_ADS_ID}`;
        document.head.appendChild(s);
      }
      window.gtag('config', PIXEL_CONFIG.GOOGLE_ADS_ID);
    } catch (e) {
      console.warn('[Pixels] Google Ads init failed', e);
    }
  }

  // Microsoft / Bing UET
  if (isConfigured(PIXEL_CONFIG.BING_UET_ID)) {
    try {
      window.uetq = window.uetq || [];
      const existing = document.querySelector('script[src*="bat.bing.com/bat.js"]');
      if (!existing) {
        const s = document.createElement('script');
        s.async = true;
        s.src = 'https://bat.bing.com/bat.js';
        s.onload = () => {
          try {
            const o: any = { ti: PIXEL_CONFIG.BING_UET_ID, q: window.uetq };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.uetq = new (window as any).UET(o);
            window.uetq.push('pageLoad');
          } catch (err) {
            console.warn('[Pixels] Bing UET init failed', err);
          }
        };
        document.head.appendChild(s);
      }
    } catch (e) {
      console.warn('[Pixels] Bing init failed', e);
    }
  }
};

// ============================================
// META CLICK/COOKIE HELPERS
// Capture _fbp / _fbc cookies and fbclid so checkout can pass them to the
// server, which uses them as CAPI user_data for better event matching.
// ============================================
const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

const ensureFbp = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  let fbp = readCookie('_fbp');
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`;
    const maxAge = 90 * 24 * 60 * 60; // 90 days
    document.cookie = `_fbp=${fbp}; max-age=${maxAge}; path=/; SameSite=Lax`;
  }
  return fbp;
};

export interface MetaAttribution {
  fbp?: string;
  fbc?: string;
}

// Call right before opening Stripe Checkout so CAPI can match the session.
export const getMetaAttribution = (): MetaAttribution => {
  if (!hasMarketingConsent()) return {};
  const fbp = ensureFbp();
  let fbc = readCookie('_fbc');
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  if (fbclid) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
    const maxAge = 90 * 24 * 60 * 60;
    document.cookie = `_fbc=${fbc}; max-age=${maxAge}; path=/; SameSite=Lax`;
  }
  return { fbp, fbc };
};
